/* The gallery shortcut scrolls to the lower video. Supported players remain
 * inert until the customer activates their facade; native gallery media stay intact.
 */
(() => {
  'use strict';

  if (window.BPLProductVideo) {
    window.BPLProductVideo.init();
    return;
  }

  const states = new Map();
  const mainSelector = '.shopify-section--product-template .bpl-product--01';
  const contentSelector = '.bpl-product-content--01[data-bpl-content-product-id]';
  const svgNamespace = 'http://www.w3.org/2000/svg';

  function isSupportedPlayer(source) {
    try {
      const url = new URL(source);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
      if (url.hostname === 'view.vzaar.com') return /^\/\d+\/player\/?$/.test(url.pathname);
      if (url.hostname === 'iframe.dacast.com') return /^\/vod\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+\/?$/.test(url.pathname);
      return false;
    } catch (_) {
      return false;
    }
  }

  function isSupportedNativeVideo(source) {
    try {
      const url = new URL(source);
      return url.protocol === 'https:' && !url.username && !url.password && /\.mp4$/i.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  function isSupportedMedia(element) {
    const source = element.getAttribute('src') || element.getAttribute('href');
    if (isSupportedPlayer(source)) return true;
    return document.body.classList.contains('product-bpl-remodel') &&
      element.matches('video[data-bpl-native-video], [data-bpl-video-native] [data-bpl-video-load]') &&
      isSupportedNativeVideo(source);
  }

  function visibleHeaderOffset() {
    let fixedBottom = 0;
    let fixedReserve = 0;
    let sourceReserve = 0;
    document.querySelectorAll('#header, [data-desktop-header], .header-fixed--true, .sticky_nav').forEach(header => {
      const style = window.getComputedStyle(header);
      const rect = header.getBoundingClientRect();
      if (style.display === 'none' || style.visibility === 'hidden') return;
      if ((style.position === 'fixed' || style.position === 'sticky') && rect.width > 0 && rect.height > 0) {
        // A transformed-out sticky copy still supplies its future visible height.
        fixedReserve = Math.max(fixedReserve, rect.height);
        if (rect.bottom > 0) fixedBottom = Math.max(fixedBottom, Math.min(rect.bottom, rect.height));
      }
      // Turbo can reveal its fixed desktop navigation only after scrolling.
      // Use the full source header only if its compact sticky copy cannot yet
      // be measured; the source must not override a visible smaller sticky bar.
      if (header.classList.contains('header-fixed--true') && rect.width > 0 && rect.height > 0) {
        sourceReserve = Math.max(sourceReserve, rect.height);
      }
    });
    return Math.ceil(Math.max(fixedBottom, fixedReserve) || sourceReserve) + 20;
  }

  function headingFor(frame, content, productId) {
    const target = frame.closest('[data-bpl-video-target]');
    if (target) return { target, heading: target.querySelector('[data-bpl-video-heading]') || target };

    // Preserve legacy embed wrappers and their source aspect ratios. The heading
    // is inserted before the highest video-only wrapper, never inside a player.
    const facade = frame.closest('[data-bpl-video-facade]');
    let wrapper = facade || frame;
    while (wrapper.parentElement && wrapper.parentElement !== content && !wrapper.parentElement.classList.contains('product-description')) {
      const parent = wrapper.parentElement;
      const surroundingText = facade ? parent.textContent.replace(facade.textContent, '').trim() : parent.textContent.trim();
      if (surroundingText || parent.querySelectorAll('iframe, video, [data-bpl-video-facade]').length !== 1 || parent.tagName === 'DETAILS') break;
      wrapper = parent;
    }
    const heading = document.createElement('h2');
    const section = content.closest('[id]');
    const suffix = (section ? section.id : productId).replace(/[^a-zA-Z0-9_-]/g, '-');
    heading.id = `bpl-product-video-${suffix}`;
    heading.className = 'bpl-product-video__heading';
    heading.textContent = 'Product video';
    heading.tabIndex = -1;
    heading.dataset.bplVideoHeading = '';
    heading.dataset.bplVideoTarget = '';
    heading.dataset.bplVideoGenerated = '';
    wrapper.insertAdjacentElement('beforebegin', heading);
    return { target: heading, heading };
  }

  function playIcon() {
    const svg = document.createElementNS(svgNamespace, 'svg');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const circle = document.createElementNS(svgNamespace, 'circle');
    circle.setAttribute('cx', '16');
    circle.setAttribute('cy', '16');
    circle.setAttribute('r', '15');
    circle.setAttribute('fill', 'currentColor');
    const triangle = document.createElementNS(svgNamespace, 'path');
    triangle.setAttribute('d', 'M13 10.5 22 16l-9 5.5Z');
    triangle.setAttribute('fill', '#fff');
    svg.append(circle, triangle);
    return svg;
  }

  function shortcut(gallery, target) {
    const rail = document.createElement('div');
    rail.className = 'bpl-gallery-video';
    rail.dataset.bplVideoShortcut = '';
    const link = document.createElement('a');
    link.className = 'bpl-gallery-video__link';
    link.href = `#${target.id}`;
    link.setAttribute('aria-label', 'Watch product video — jump to video section');
    const preview = document.createElement('span');
    preview.className = 'bpl-gallery-video__preview';
    preview.setAttribute('aria-hidden', 'true');
    // Reuse an existing product image as a clearly marked shortcut preview, not
    // as a claimed video poster. No provider thumbnail requests are necessary.
    const source = gallery.querySelector('.product_gallery img.product-gallery__image');
    const imageSource = source && [source.currentSrc, source.getAttribute('src'), source.getAttribute('data-src')]
      .find(value => value && /^(https?:)?\/\//.test(value) && !/[{}]/.test(value));
    if (imageSource) {
      const image = document.createElement('img');
      image.src = imageSource;
      image.alt = '';
      image.width = 78;
      image.height = 64;
      image.loading = 'lazy';
      preview.appendChild(image);
    }
    preview.appendChild(playIcon());
    const label = document.createElement('span');
    label.textContent = 'Watch product video';
    link.append(preview, label);
    rail.appendChild(link);
    // This sibling rail deliberately has no gallery-cell class: adding a lower
    // embed to Turbo's thumbnail track would desynchronize native media indexes.
    gallery.appendChild(rail);
    return { rail, link };
  }

  function detach(main) {
    const state = states.get(main);
    if (!state) return;
    state.link.removeEventListener('click', state.onClick);
    state.rail.remove();
    if (state.heading.hasAttribute('data-bpl-video-generated')) state.heading.remove();
    states.delete(main);
  }

  function loadVideo(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target && event.target.closest && event.target.closest('[data-bpl-video-load]');
    const facade = link && link.closest('[data-bpl-video-facade]');
    if (!facade || !facade.isConnected || !facade.closest(contentSelector)) return;
    const template = facade.querySelector('template[data-bpl-video-player]');
    const source = template && template.content.querySelector('iframe[src], video[data-bpl-native-video][src]');
    if (!source || !isSupportedMedia(source) || source.getAttribute('src') !== link.getAttribute('href')) return;
    event.preventDefault();
    const frame = source.cloneNode(true);
    // The original URL and player permissions remain unchanged. Loading now is
    // intentional; do not wait for the browser's lazy-frame threshold again.
    const native = frame.tagName === 'VIDEO';
    if (native) {
      frame.addEventListener('error', () => {
        if (!frame.isConnected) return;
        link.removeAttribute('data-bpl-video-load');
        frame.replaceWith(facade);
        link.focus({ preventScroll: true });
        init();
      }, { once: true });
    } else frame.loading = 'eager';
    facade.replaceWith(frame);
    frame.focus({ preventScroll: true });
    if (native) {
      const playback = frame.play();
      if (playback && typeof playback.catch === 'function') playback.catch(() => {});
    }
    init();
  }

  function attach(main) {
    const productId = main.dataset.bplProductId || (main.querySelector('[data-product-form]') || {}).dataset?.productId;
    const content = Array.from(document.querySelectorAll(contentSelector)).find(item => item.dataset.bplContentProductId === productId);
    const gallery = main.querySelector('.gallery-wrap');
    const frame = content && Array.from(content.querySelectorAll('.product-description iframe[src], .product-description [data-bpl-video-load], .product-description video[data-bpl-native-video][src]')).find(isSupportedMedia);
    const existing = states.get(main);
    if (existing && existing.content === content && existing.frame === frame && existing.rail.isConnected) return;
    if (existing) detach(main);
    if (!content || !gallery || !frame) return;

    const { target, heading } = headingFor(frame, content, productId);
    const { rail, link } = shortcut(gallery, target);
    const onClick = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      // A legacy author may have placed an embed inside a disclosure. Open only
      // its ancestors before scrolling so the destination cannot remain hidden.
      let ancestor = frame.parentElement;
      while (ancestor && ancestor !== content) {
        if (ancestor.tagName === 'DETAILS') ancestor.open = true;
        ancestor = ancestor.parentElement;
      }
      const offset = visibleHeaderOffset();
      target.style.setProperty('--bpl-video-scroll-offset', `${offset}px`);
      heading.focus({ preventScroll: true });
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: reducedMotion ? 'instant' : 'smooth' });
      // The player remains paused. Playback is a second, explicit user action.
    };
    link.addEventListener('click', onClick);
    states.set(main, { content, frame, heading, rail, link, onClick });
  }

  function init() {
    states.forEach((_, main) => { if (!main.isConnected || !main.matches(mainSelector)) detach(main); });
    document.querySelectorAll(mainSelector).forEach(attach);
  }

  function unload(root) {
    states.forEach((state, main) => {
      if (root === main || root.contains(main) || root === state.content || root.contains(state.content)) detach(main);
    });
  }

  window.BPLProductVideo = { init, unload };
  document.addEventListener('click', loadVideo);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('load', init, { once: true });
})();
